import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import baseTheme from '../theme';
import api from '../services/api';
import { useAuth } from './AuthContext';

const BrandingContext = createContext();

// Default neutral colors
const defaultBranding = {
  customerName: '',
  primaryColor: '#2c3e50', // Dark blue-grey
  secondaryColor: '#34495e', // Slightly lighter blue-grey
  logoUrl: null,
  favicon: null,
  customCss: null,
  customHeader: null,
  customFooter: null,
  isLoading: false,
  error: null
};

export const BrandingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [branding, setBranding] = useState(defaultBranding);

  // Create a theme that extends the base theme with branding colors
  const theme = useMemo(() => {
    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          main: branding.primaryColor || defaultBranding.primaryColor,
        },
        secondary: {
          main: branding.secondaryColor || defaultBranding.secondaryColor,
        },
      },
    });
  }, [branding.primaryColor, branding.secondaryColor]);

  useEffect(() => {
    let isMounted = true;

    const fetchBranding = async () => {
      if (!isAuthenticated) return;

      setBranding(prev => ({ ...prev, isLoading: true }));
      
      try {
        const response = await api.get('/api/branding');
        if (isMounted) {
          // Merge response data with defaults for any missing values
          const brandingData = {
            ...defaultBranding,
            ...response.data,
            isLoading: false,
            error: null
          };

          setBranding(brandingData);
          
          // Apply custom CSS if provided
          if (brandingData.customCss) {
            const style = document.createElement('style');
            style.textContent = brandingData.customCss;
            document.head.appendChild(style);
          }

          // Update favicon if provided
          if (brandingData.favicon) {
            const link = document.querySelector("link[rel~='icon']");
            if (link) {
              link.href = brandingData.favicon;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
        if (isMounted) {
          setBranding(prev => ({
            ...defaultBranding,
            isLoading: false,
            error: 'Failed to load branding'
          }));
        }
      }
    };

    fetchBranding();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const value = {
    ...branding,
    theme
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext; 